import React, {useEffect, useMemo, useState} from 'react';
import Dialog from "../../components/controls/Dialog";
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FEConfigOptionalForm from './FEConfigOptionalForm';
import * as feconfigService from '../../services/feconfigService';
import {Form, useForm} from '../../components/useForm';
import useAlert from '../../components/useAlert';
import {Typography} from '@mui/material';
import useLinearProgress from '../../components/useLinearProgress';
import JsonTextarea, {parseJson} from '../../components/controls/JsonTextarea';

const feConfig = {}
const defaultConfigJsonName = "defaultConfigJson"

const FEConfigDialog = (props) => {
    const {open, selectedRow} = props;
    const [options, setOptions] = useState([]);
    const initialFValues = {
        defaultConfigJson: null,
    };

    useEffect(() => {
        if (open) {
            setIsLoading(true);
            handleAlertClose();
            resetDialog();
            feconfigService.getAll(selectedRow.id).then((response) => {
                const result = response.data;
                if (result) {
                    if (result.defaultConfigJson) {
                        handleJSONInputChange(result.defaultConfigJson)
                    }
                    if (result.items)
                        setOptions(result.items);
                }
            }).catch(error => {
                showError(error.response.data.message);
            }).finally(() => {
                setIsLoading(false);
            })
        }
        //eslint-disable-next-line
    }, [open])

    const {
        values,
        setValues,
        errors,
        handleClose,
        handleError
    } = useForm(initialFValues, props);

    const {
        Alert,
        status,
        showSuccess,
        showError,
        handleAlertClose
    } = useAlert();

    const {
        LinearPorgress,
        isLoading,
        setIsLoading
    } = useLinearProgress(true);

    const jsonErrors = useMemo(() => {
        return [
            parseJson(values.defaultConfigJson).error,
            ...options.map(it => parseJson(it.configJson).error)
        ].filter(it => it);
    }, [values.defaultConfigJson, options]);

    const submit = () => {
        if (jsonErrors.length > 0)
            return;
        const request = {
            defaultConfigJson: values.defaultConfigJson && values.defaultConfigJson.trim().length > 0 ? values.defaultConfigJson : null
        }
        if (options.length > 0)
            request.items = options
        else
            delete request.items

        feconfigService.update(selectedRow.id, request).then(() => {
            showSuccess();
            resetDialog();
            handleClose();
        })
            .catch(error => {
                handleError(error.response.data.fields);
                showError(error.response.data.message);
            }).finally(() => {
        })
    };

    const handleJSONInputChange = (input) => {
        setValues({
            ...values,
            [defaultConfigJsonName]: input
        })
    }

    const resetDialog = () => {
        setOptions([]);
    }

    return (
        <Form>
            {status ? <Alert/> : null}
            <Dialog
                title="Frontend JSON Configuration"
                open={open}
                fullWidth={true}
                handleClose={() => {
                    resetDialog();
                    handleClose();
                }
                }>
                {isLoading ? <LinearPorgress/> :
                    <React.Fragment>
                        <DialogContent>
                            <JsonTextarea
                                value={values.defaultConfigJson}
                                placeholder={"Default JSON"}
                                onChange={handleJSONInputChange}
                                error={errors.defaultConfigJson}
                            />
                            {options.length > 0 &&
                            <Typography mt={2} variant="h6" gutterBottom component="div">Optionals</Typography>}
                            {options.map((it, index) => {
                                return (
                                    <React.Fragment key={index}>
                                        <FEConfigOptionalForm
                                            options={options}
                                            setOptions={setOptions}
                                            index={index}
                                            errors={errors}
                                        />
                                    </React.Fragment>
                                )
                            })}
                        </DialogContent>
                        <DialogActions>
                            {options.length > 0 ?
                                <Button onClick={() => setOptions(options.slice(0, -1))}>Delete</Button> : null}
                            <Button onClick={() => {
                                setOptions(it => [...it, feConfig]);
                            }
                            }>Add Optional</Button>
                        </DialogActions>
                        <DialogActions>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    resetDialog();
                                    handleClose();
                                }}>Cancel</Button>
                            <Button variant="contained" disabled={jsonErrors.length > 0}
                                    onClick={submit}>Confirm</Button>
                        </DialogActions>
                    </React.Fragment>
                }
            </Dialog>
        </Form>
    );
}

export default FEConfigDialog;
